/* eslint-disable enforce-ids-in-jsx/missing-ids */
import React from 'react'
import styled, { keyframes } from 'styled-components'

import { SIMULATION_STATUSES_VALUES } from 'containers/SpearPhishingAgent/constants'

import Checkbox from 'components/lib/DesignSystem/V3/Checkbox/Checkbox'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

import { formatDayDate, formatFullDate } from '../../utils'
import { SimulationStatusBadge } from '../SimulationSummary'

const SelectCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

/* The V3 Table pads its first header cell 24px and its first body cell 16px, so a
   centred checkbox lands 5px apart between the two. Compensate in the header until
   the Table aligns them itself. */
const HeaderSelectCell = styled(SelectCell)`
  width: var(--t-spacing-4);
  margin-left: calc(var(--t-spacing-4) - var(--t-spacing-6));
`

const RateWithChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-2);
  color: var(--color-text);
  white-space: nowrap;
`

/* MOTION (TBD proposal): when the pending count changes (someone finished the training and
   the list refreshed), the new number rolls up into place instead of blinking. Keyed on the
   value, so it plays only on change. */
const countRoll = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
`

const RollingCount = styled.span`
  display: inline-block;
  animation: ${countRoll} 200ms cubic-bezier(0, 0, 0.2, 1) backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/* Attribute chip in the training report's idiom (like its "Late" / "Not delivered"):
   thin outline, no fill, no glyph. It reads as progress on the people who clicked, not
   as a label on a person. */
const TrainingProgressChip = styled.span`
  display: inline-flex;
  align-items: center;
  /* The rolling count is its own flex item, so the word space comes from the gap. */
  gap: var(--t-spacing-1);
  height: var(--t-spacing-5);
  padding: 0 var(--t-spacing-2);
  border-radius: var(--t-border-radius-rounded);
  border: 1px solid var(--warning-text);
  color: var(--warning-text);
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-xs);
  white-space: nowrap;
`

const TitleCell = styled.div`
  color: var(--color-text);
  font-weight: var(--t-typography-font-weight-regular);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MutedCell = styled.div`
  color: var(--sub-text-color);
  white-space: nowrap;
`

const RateCell = styled.div`
  color: var(--color-text);
  white-space: nowrap;
`

/* A simulation still going out has no results yet, so a short dash says "not available",
   where 0% would read as "nobody clicked". */
const isStillSending = ({ status }) => status === SIMULATION_STATUSES_VALUES.SENDING

const createSimulationColumns = ({ selectedIds, visibleIds, onToggleSimulation, onToggleAll }) => {
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id))
  const someSelected = visibleIds.some((id) => selectedIds.includes(id))

  return [
    {
      key: 'select',
      width: '48px',
      header: (
        <HeaderSelectCell>
          <Checkbox
            id="Psima_Launched_SelectAll_checkbox"
            dataTestId="Psima_Launched_SelectAll_checkbox"
            label=""
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onChange={() => onToggleAll(visibleIds)}
          />
        </HeaderSelectCell>
      ),
      render: ({ id }) => (
        <SelectCell onClick={(event) => event.stopPropagation()}>
          <Checkbox
            id={`Psima_Launched_SimulationCheckbox_${id}`}
            dataTestId={`Psima_Launched_SimulationCheckbox_${id}`}
            label=""
            checked={selectedIds.includes(id)}
            onChange={() => onToggleSimulation(id)}
          />
        </SelectCell>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: ({ id, title }) => (
        <TitleCell title={title} data-testid={`PsimaLaunched_TableTitle_div-${id}`}>
          {title}
        </TitleCell>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (simulation) => <SimulationStatusBadge simulation={simulation} />,
    },
    {
      key: 'participantsCount',
      header: 'Participants',
      sortable: true,
      render: ({ id, participantsCount }) => (
        <RateCell data-testid={`PsimaLaunched_Participants_div-${id}`}>
          {participantsCount}
        </RateCell>
      ),
    },
    {
      key: 'clickRate',
      header: 'Click rate',
      sortable: true,
      render: (simulation) => {
        const { id, untrainedCount: untrained = 0, clickedCount: clicked = 0 } = simulation

        if (isStillSending(simulation)) {
          return <RateCell data-testid={`PsimaLaunched_ClickRate_div-${id}`}>–</RateCell>
        }

        // Post-click training only means something against the people who clicked, so it
        // sits on the click rate rather than in a column of its own: the count still owing
        // ("2 pending training"), shown only while someone has training to do (Natali, 3.9).
        const trained = clicked - untrained

        return (
          <RateWithChip data-testid={`PsimaLaunched_ClickRate_div-${id}`}>
            {`${simulation.engagement?.clicked ?? 0}%`}
            {untrained > 0 && (
              <Tooltip
                id={`PsimaLaunched_Untrained_tooltip-${id}`}
                side="top"
                arrow
                content={`${trained} of the ${clicked} ${
                  clicked === 1 ? 'employee who clicked has' : 'employees who clicked have'
                } completed the training. ${untrained} still ${
                  untrained === 1 ? 'has' : 'have'
                } it pending.`}
              >
                <TrainingProgressChip data-testid={`PsimaLaunched_Untrained_div-${id}`}>
                  <RollingCount key={untrained}>{untrained}</RollingCount>
                  pending training
                </TrainingProgressChip>
              </Tooltip>
            )}
          </RateWithChip>
        )
      },
    },
    {
      key: 'reportRate',
      header: 'Report rate',
      sortable: true,
      render: (simulation) => (
        <RateCell data-testid={`PsimaLaunched_ReportRate_div-${simulation.id}`}>
          {isStillSending(simulation) ? '–' : `${simulation.engagement?.reported ?? 0}%`}
        </RateCell>
      ),
    },
    {
      key: 'departments',
      header: 'Departments',
      render: ({ id, departments }) => (
        <MutedCell data-testid={`PsimaLaunched_Department_div-${id}`}>
          {departments?.length ? departments.join(', ') : '–'}
        </MutedCell>
      ),
    },
    {
      key: 'createdAt',
      header: 'Launch date',
      sortable: true,
      render: ({ id, createdAt }) => (
        <MutedCell data-testid={`PsimaLaunched_LaunchDate_div-${id}`}>
          {formatFullDate(createdAt)}
        </MutedCell>
      ),
    },
    {
      key: 'lastEngagementAt',
      header: 'Last engagement',
      sortable: true,
      render: ({ id, lastEngagementAt }) => (
        <MutedCell data-testid={`PsimaLaunched_LastEngagement_div-${id}`}>
          {lastEngagementAt ? formatDayDate(lastEngagementAt) : 'No engagement yet'}
        </MutedCell>
      ),
    },
  ]
}

export default createSimulationColumns
